import React, { useMemo, useState, useEffect, useRef, useContext, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import GridDesign from "../../GridDesign/GridDesign";

import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import axios from "axios";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AuthContext } from "../../../context/AuthContext";
import { ClipLoader } from "react-spinners";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "../../../app/settingsSlice";
import { selectActiveFyid } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";

const getDefaultColumns = (reportType) => {
  const isSimple = reportType === "simpleReport";
  const list = [
    { key: "schemeName", label: "Scheme Name", width: 128 },
    { key: "accNo", label: "AccNo", width: 80, align: "right" },
    { key: "customer", label: "Customer", width: 128 },
    { key: "accStatus", label: "Status", width: 85, align: "center", show: false },
    { key: "expiryDate", label: "Expiry Date", width: 95, align: "center", show: false },
    { key: "entryNo", label: "Entryno", width: 80 },
    { key: "type", label: "Type", width: 150 },
    { key: "rate", label: "Rate", width: 80, align: "right" },
    { key: "amount", label: "Amount", width: 112, align: "right" },
    { key: "cashBank", label: "CASH/BANK", width: 112, show: !isSimple },
    { key: "cash", label: "CASH", width: 96, align: "right", show: !isSimple },
    { key: "bank", label: "BANK", width: 96, align: "right", show: !isSimple },
    { key: "date", label: "Date", width: 96 },
    { key: "days", label: "Days", width: 64, align: "center" },
    { key: "intPerc", label: "int%", width: 64, align: "right", show: isSimple },
    { key: "interest", label: "%amnt", width: 112, align: "right", show: isSimple },
    { key: "weight", label: "Weight", width: 112, align: "right" },
    { key: "redeemRate", label: "Redeem Rate", width: 112, align: "right" },
    { key: "redeemAmount", label: "Redeem Amount", width: 128, align: "right" },
    { key: "redeemWeight", label: "Redeem Weight", width: 128, align: "right" },
    { key: "schemeBalance", label: "Scheme Balance", width: 128, align: "right" },
    { key: "plWeight", label: "P/L in Weight", width: 128, align: "right" },
    { key: "agent", label: "Agent", width: 112 },
    { key: "closedDate", label: "Closed Date", width: 95, align: "center", show: false },
    { key: "narration", label: "Narration", width: 160 }
  ];

  return list.map((col, idx) => ({
    ...col,
    columnName: col.key,
    header: col.label,
    orderNo: idx,
    show: col.show !== undefined ? col.show : true
  }));
};

function SchemeReport({ onClose, reportData, forms, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const GeneralSettings = useSelector(selectGeneralSettings);
  const [roundOff, setRoundOff] = useState(2);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const reportType = reportData.reportType || "simpleReport";
  const formName = `Scheme Report - ${reportType}`;

  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns(reportType));
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);

  // Load grid settings
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode || !formName) return;

      const defaults = getDefaultColumns(reportType);

      // Check cache first
      let cached = localStorage.getItem(`grid_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const sanitized = parsed.map((c) => {
            if (c.key === "accStatus" || c.key === "expiryDate" || c.key === "closedDate") {
              return { ...c, show: false };
            }
            return c;
          });
          setGridColumns(sanitized);
          return;
        } catch (e) {
          console.error("Error parsing cached grid settings:", e);
        }
      }

      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`
        );
        const dbCols = response.data.recordset || response.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((initCol) => {
            const dbMatch = dbCols.find(
              (c) =>
                String(c.ColName).toLowerCase() ===
                String(initCol.columnName).toLowerCase()
            );
            if (dbMatch) {
              return {
                ...initCol,
                width: dbMatch.Width !== null ? dbMatch.Width : initCol.width,
                orderNo: dbMatch.InNo !== null ? dbMatch.InNo : initCol.orderNo,
                header: dbMatch.Header || initCol.header,
                show: dbMatch.Vis === 1
              };
            }
            return initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${formName}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else {
          setGridColumns(defaults);
        }
      } catch (error) {
        console.error("Error loading grid settings:", error);
        setGridColumns(defaults);
      }
    };
    loadGridSettings();
  }, [dbCode, apiBaseUrl, formName, reportType]);

  const visibleColumns = useMemo(
    () => gridColumns.filter((col) => col.show),
    [gridColumns]
  );

  const gridTemplateColumns = useMemo(
    () => visibleColumns.map((col) => `${col.width}px`).join(" "),
    [visibleColumns]
  );

  // Load Decimal Settings
  useEffect(() => {
    const obj = GeneralSettings.filter(
      (item) => item.Name === "DECIMAL POINT IN REPORT"
    );
    if (obj.length > 0 && obj[0].s_Value) {
      setRoundOff(parseInt(obj[0].s_Value));
    }
  }, [GeneralSettings]);

  const fyid = useSelector(selectActiveFyid);
  const activeLocation = useSelector(selectActiveLocation);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const effectiveFyid = (reportData.fyid !== undefined && reportData.fyid !== null && reportData.fyid !== "") 
        ? Number(reportData.fyid) 
        : (fyid || 0);
      const effectiveLocationId = (reportData.locationId !== undefined && reportData.locationId !== null && reportData.locationId !== "")
        ? Number(reportData.locationId)
        : 0;
      const requestPayload = { 
        reportData: { 
          ...reportData, 
          fyid: effectiveFyid,
          locationId: effectiveLocationId
        }, 
        forms 
      };
      const response = await axios.post(
        `${apiBaseUrl}/main/Schrepsimple/${dbCode}`,
        requestPayload
      );
      setTableData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error", "Failed to load report data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiBaseUrl, reportData, forms, dbCode]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const formatNumber = (value, decimals = roundOff) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "null"
    )
      return "";
    const numValue =
      typeof value === "string" ? parseFloat(value) : Number(value);
    if (isNaN(numValue)) return "";
    return numValue.toFixed(decimals);
  };

  const formatValue = (row, key) => {
    switch (key) {
      case "schemeName": return row.schemeName || "";
      case "accNo": return row.accno || "";
      case "customer": return row.ledname || "";
      case "accStatus": return row.atType === "Sub Total" ? (row.accStatus || "") : "";
      case "expiryDate": return row.atType === "Sub Total" ? (row.expiryDate || "") : "";
      case "closedDate": return row.atType === "Sub Total" ? (row.closedDate || "") : "";
      case "entryNo": return `${row.atEntryno}-${row.Agent?.substring(0, 3) || ""}`;
      case "type": return row.atType || "";
      case "rate": return row.rate || "";
      case "amount": return formatNumber(row.atCreditAmount);
      case "cashBank": return row.atOpposite || "";
      case "cash": return formatNumber(row.CashAmount);
      case "bank": return formatNumber(row.BankAmount);
      case "date": return row.atdate || "";
      case "days": return row.dayss || "";
      case "intPerc": return row.intrPerc || "";
      case "interest": return formatNumber(row.IntrestAmount);
      case "weight": return formatNumber(row.atCreditWeight);
      case "redeemRate": return row.redeemrate || "";
      case "redeemAmount": return formatNumber(row.atDebitAmount);
      case "redeemWeight": return formatNumber(row.atDebitWeight);
      case "schemeBalance": return formatNumber(row.schemeBalance);
      case "plWeight": return formatNumber(row.BalanceWeight);
      case "agent": return row.Agent || "";
      case "narration": return row.atNarration || "";
      default: return "";
    }
  };

  // Client-side quick filter
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      visibleColumns.some((col) => {
        const val = formatValue(row, col.key);
        return val.toLowerCase().includes(query);
      })
    );
  }, [tableData, searchQuery, visibleColumns]);

  // ==================== HIGH-PERFORMANCE AUTO-ADVANCE PAGINATION ====================
  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const { exportingText, runExport } = useReportExport();

  const exportToCsv = () => {
    runExport("Generating Excel / CSV...", () => {
      const header = visibleColumns.map((col) => col.header).join(",");
      const rows = filteredRows.map((row) =>
        visibleColumns
          .map((col) => `"${String(formatValue(row, col.key)).replace(/"/g, '""')}"`)
          .join(",")
      );

      const csv = [header, ...rows].join("\n");
      const link = document.createElement("a");
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
      link.download = "scheme_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const exportToFullPDF = () => {
    runExport("Generating PDF Report...", () => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a2",
      });

      const columns = visibleColumns.map((col, idx) => ({
        header: col.header,
        dataKey: `col${idx + 1}`,
      }));

      const rows = filteredRows.map((item) => {
        const row = {};
        visibleColumns.forEach((col, idx) => {
          row[`col${idx + 1}`] = formatValue(item, col.key);
        });
        return row;
      });

      doc.text("Scheme Report", 15, 20);
      doc.autoTable({
        columns,
        body: rows,
        startY: 30,
        margin: { top: 30 },
        styles: { fontSize: 8 },
      });

      doc.save("scheme_report.pdf");
    });
  };

  // ==================== COLUMN RESIZE HANDLER ====================
  // ResizableHeader is imported from shared/ResizableHeader.jsx (stable reference)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const spinnerProps = {
    color: "#026CC3",
    height: 12,
    width: 5,
    radius: 1,
    margin: 3,
    size: 30,
    speedMultiplier: 0.8,
  };

  const getReportTypeLabel = (val) => {
    switch (val) {
      case "simpleReport": return "Simple";
      case "reportOrderbyEntryno": return "Order by entry no";
      case "cabaReport": return "Cash/Bank Report";
      case "closedAccountsReport": return "Closed Account Reports";
      case "closedAccounts": return "Closed Accounts";
      default: return val || "Simple";
    }
  };

  return (
    <div
      className="bg-white mt-[2vh] sm:mt-0 font-normal w-[98vw] h-[96vh] rounded-[5px] shadow-md flex flex-col items-center absolute transition-all duration-300 md:w-[98%] md:h-[98%]"
      style={{ zIndex }}
    >
      <style>{`
        .scheme-report-border {
          border: 1px solid #ffffff;
        }
        .report-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .report-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .report-scroll::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .report-scroll::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>

      <WindowHeader
        onClose={onClose}
        title="Scheme Report"
        logo={headeLogo}
        style={{ height: "40px" }}
        className="bg-[#0f56d3] text-white"
        titleClassName="text-[13px] font-semibold"
        closeBtnClassName="w-7 h-7 rounded-[5px] border-none mr-2 flex items-center justify-center"
      />

      <ActionToolbar
        size="small"
        iconSize={16}
        buttonHeight="36px"
        showSave={false}
        showPrint
        showClear={false}
        showFind={false}
        showEdit={false}
        showDelete={false}
        showExit
        showPdf
        showExcel
        showSettingsBar
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        showNavigation={false}
        onPrint={() => window.print()}
        onExcel={exportToCsv}
        onPdf={exportToFullPDF}
        onExit={onClose}
        settingsOptions={[
          {
            label: "Design Columns",
            onClick: () => setShowGridDesignModal(true),
          }
        ]}
      />

      <div className="w-full flex-1 min-h-0 flex flex-col bg-white">
        
        {/* Info panel + Search Bar */}
        <div className="w-full border-b border-gray-200 bg-gradient-to-r from-slate-50 to-blue-50/20 px-4 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-Inter text-slate-700 flex-1">
            
            {/* Period Badge */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-slate-500 font-Inter">Period:</span>
              <span className="font-medium text-slate-900 font-Inter">{reportData.fromDate || "-"} to {reportData.toDate || "-"}</span>
            </div>

            {/* Report Type Badge */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H3a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2z" />
              </svg>
              <span className="font-semibold text-slate-500 font-Inter">Report:</span>
              <span className="font-medium text-indigo-700 font-Inter">{getReportTypeLabel(reportData.reportType)}</span>
            </div>

            {/* Status Badge in Top Info Panel */}
            {(() => {
              const statusRows = tableData.filter((r) => r.accStatus);
              if (statusRows.length === 0) return null;
              
              // If filtering by a specific account, or all returned rows share the exact same status
              const uniqueStatuses = [...new Set(statusRows.map((r) => r.accStatus))];
              if (!reportData.accno && uniqueStatuses.length > 1) return null;

              const statusRow = statusRows[0];
              const status = statusRow.accStatus;
              const exp = statusRow.expiryDate;
              const closed = statusRow.closedDate;
              if (!status) return null;
              return (
                <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                  {status === "CLOSED" ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700">
                      <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                      Status: <span className="text-slate-900 font-extrabold">CLOSED {closed ? `(${closed})` : ""}</span>
                    </span>
                  ) : status === "EXPIRED" ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-rose-700">
                      <span className="w-2 h-2 rounded-full bg-rose-600 animate-pulse"></span>
                      Status: <span className="text-rose-900 font-extrabold">EXPIRED {exp ? `(${exp})` : ""}</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                      Status: <span className="text-emerald-900 font-extrabold">ACTIVE {exp ? `(Exp: ${exp})` : ""}</span>
                    </span>
                  )}
                </div>
              );
            })()}


            {/* Customer Badge */}
            {reportData.customerName && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-semibold text-slate-500 font-Inter">Customer:</span>
                <span className="font-medium text-slate-900 font-Inter">{reportData.customerName}</span>
              </div>
            )}

            {/* Agent Badge */}
            {reportData.agentName && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-semibold text-slate-500 font-Inter">Agent:</span>
                <span className="font-medium text-slate-900 font-Inter">{reportData.agentName}</span>
              </div>
            )}

            {/* Account Number Badge */}
            {reportData.accno && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold text-slate-500 font-Inter">Acc No:</span>
                <span className="font-medium text-slate-900 font-Inter">{reportData.accno}</span>
              </div>
            )}

            {/* Phone Number Badge */}
            {reportData.mobileNo && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-semibold text-slate-500 font-Inter">Phone:</span>
                <span className="font-medium text-slate-900 font-Inter">{reportData.mobileNo}</span>
              </div>
            )}

          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <input
                type="text"
                placeholder="Quick search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1 bg-white border border-gray-300 rounded-[5px] text-[11px] font-Inter focus:outline-none focus:border-blue-500 placeholder-slate-400"
              />
              <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Table Body Container */}
        <div className="w-full flex-1 min-h-0 flex flex-col">
          {isLoading ? (
            <div className="flex justify-center items-center h-full w-full bg-slate-55/10">
              <ClipLoader {...spinnerProps} />
            </div>
          ) : (
            <>
              <div
                ref={tableContainerRef}
                className="w-full flex-1 overflow-auto report-scroll flex flex-col text-slate-800 text-[11px] font-semibold outline-none focus:ring-1 focus:ring-blue-400"
                tabIndex={0}
                onScroll={handleScroll}
                onWheel={handleWheel}
                onKeyDown={handleKeyDown}
              >
                <div className="min-w-max flex flex-col flex-1">
                
                {/* Sticky Header Row */}
                <div className="sticky top-0 z-10 bg-[#0f56d3] scheme-report-border shrink-0">
                  <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                    {visibleColumns.map((col) => (
                      <ResizableHeader key={col.key} col={col} formName={formName} onResize={handleColumnResize}>
                        <div className="text-white truncate text-center font-bold font-Inter scheme-report-border text-[11px] w-full h-full flex items-center justify-center px-1">
                          {col.header}
                        </div>
                      </ResizableHeader>
                    ))}
                  </div>
                </div>

                {/* Data Scrollable Body */}
                <div className="bg-gray-100 flex-1">
                  {displayedRows.map((rowData, rowIndex) => {
                    const isSelected = rowIndex === selectedIndex;
                    const isTotalRow = rowData.atType === "Sub Total" || rowData.atType === "Total";
                    const prevRow = rowIndex > 0 ? displayedRows[rowIndex - 1] : null;
                    const isFirstRowOfAccount = Boolean(rowData.accno) &&
                      rowData.atType !== "Total" &&
                      rowData.atType !== "Sub Total" &&
                      (!prevRow || prevRow.accno !== rowData.accno);

                    return (
                      <React.Fragment key={rowIndex}>
                        {/* Account Group Header Banner */}
                        {isFirstRowOfAccount && (
                          <div className="sticky left-0 bg-gradient-to-r from-blue-50/90 via-slate-100 to-white border-y border-blue-200/80 px-3 py-1.5 flex items-center justify-between gap-4 shadow-xs z-[5]">
                            <div className="flex flex-wrap items-center gap-2.5">
                              {/* Status Badge on the Left Side */}
                              {rowData.accStatus === "CLOSED" ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-800 border border-slate-300 shadow-2xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                                  CLOSED {rowData.closedDate ? `(${rowData.closedDate})` : ""}
                                </span>
                              ) : rowData.accStatus === "EXPIRED" ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-900 border border-rose-300 shadow-2xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse"></span>
                                  EXPIRED on {rowData.expiryDate || "Scheme End Date"} • (Interest Frozen)
                                </span>
                              ) : rowData.accStatus === "ACTIVE" ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300 shadow-2xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                  ACTIVE {rowData.expiryDate ? `(Expires: ${rowData.expiryDate})` : ""}
                                </span>
                              ) : null}

                              <div className="flex items-center gap-1.5 font-bold text-slate-900 text-[11px] font-Inter">
                                <span className="px-1.5 py-0.5 rounded bg-[#0f56d3] text-white font-extrabold text-[10px] tracking-wide">
                                  A/C
                                </span>
                                <span className="text-[#0f56d3] font-extrabold">{rowData.accno}</span>
                              </div>
                              {rowData.ledname && (
                                <span className="text-slate-800 font-semibold text-[11px] font-Inter flex items-center gap-1">
                                  <span className="text-slate-400 font-normal">|</span>
                                  <span>{rowData.ledname}</span>
                                </span>
                              )}
                              {rowData.schemeName && (
                                <span className="text-slate-600 font-medium text-[10px] font-Inter bg-white px-2 py-0.5 rounded border border-slate-200 shadow-2xs">
                                  {rowData.schemeName}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div
                          key={rowIndex}
                          data-row-index={rowIndex}
                          onClick={() => setSelectedIndex(rowIndex)}
                          className={`grid gap-0 h-8 border-b border-gray-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${
                            isSelected
                              ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                              : rowData.atType === "Sub Total"
                              ? "bg-red-50 text-red-700 font-bold"
                              : rowData.atType === "Total"
                                ? "bg-[#dbeafe] text-blue-800 font-bold"
                                : rowIndex % 2 === 0
                                  ? "bg-[#f7f7f7]"
                                  : "bg-[#eeeeee]"
                          }`}
                          style={{ gridTemplateColumns }}
                        >
                          {visibleColumns.map((col) => {
                            if (col.key === "accStatus") {
                              if (rowData.atType === "Sub Total" && rowData.accStatus) {
                                const isExp = rowData.accStatus === "EXPIRED";
                                const isClosed = rowData.accStatus === "CLOSED";
                                const isAct = rowData.accStatus === "ACTIVE";
                                return (
                                  <div
                                    key={col.key}
                                    className="font-Inter text-[11px] scheme-report-border px-1 flex items-center justify-center truncate"
                                  >
                                    <span
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                        isExp
                                          ? "bg-rose-100 text-rose-800 border border-rose-300"
                                          : isClosed
                                            ? "bg-slate-200 text-slate-700 border border-slate-300"
                                            : isAct
                                              ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                              : ""
                                      }`}
                                    >
                                      {rowData.accStatus}
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={col.key}
                                  className="font-Inter text-[11px] scheme-report-border px-2 flex items-center justify-center truncate"
                                />
                              );
                            }

                            return (
                              <div
                                key={col.key}
                                className={`font-Inter text-[11px] scheme-report-border px-2 flex items-center truncate ${
                                  col.align === "right" ? "justify-end text-right" : 
                                  col.align === "center" ? "justify-center text-center" : ""
                                }`}
                              >
                                {formatValue(rowData, col.key)}
                              </div>
                            );
                          })}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

              </div>
            </div>
            <TablePagination
              currentPage={currentPage}
              totalRows={totalRows}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              autoAdvance={autoAdvance}
              onToggleAutoAdvance={toggleAutoAdvance}
              isFlipping={isFlipping}
            />
          </>
        )}
      </div>

      </div>

      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => {
            setGridColumns(updated);
            setShowGridDesignModal(false);
          }}
          formName={formName}
          initialColumns={getDefaultColumns(reportType)}
          zIndex={zIndex}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default SchemeReport;
